import type { SelectOptionPropsType } from '@Shelly/shelly-design-system';
import { format } from 'svelte-i18n';
import { get } from 'svelte/store';
import type { CuryActionData, CuryInitValues, CuryActionConfig } from 'src/types/Cury';

/**
 * Action configuration mapping for Cury diffuser
 */
export const curyActionConfigs: CuryActionConfig[] = [
	{
		key: 'on',
		slots: ['left', 'right'],
		value: true,
		formatKey: 'cury_turn_on_{slot}',
	},
	{
		key: 'off',
		slots: ['left', 'right'],
		value: false,
		formatKey: 'cury_turn_off_{slot}',
	},
	{
		key: 'boost',
		slots: ['left', 'right'],
		value: true,
		formatKey: 'cury_boost_{slot}',
	},
	{
		key: 'stop',
		slots: ['left', 'right'],
		value: true,
		formatKey: 'cury_boost_stop_{slot}',
	},
	{
		key: 'set',
		slots: [],
		value: null,
		formatKey: 'label_set_intensity',
		requiresSlot: true,
	},
	{
		key: 'setconfig',
		slots: [],
		value: null,
		formatKey: 'cury_set_ambient_light',
		requiresSlot: false,
	},
];

/**
 * Generate action options for Cury diffuser dynamically
 */
export const generateCuryActionOptions = (): SelectOptionPropsType[] => {
	const _format = get(format);
	//Placeholder for select action with placeholder value 0 which is not in the action options
	const selectActionPlaceholder = {
		title: _format('select_action', { default: 'Select action' }),
		value: 0,
	};

	const actionOptions = curyActionConfigs.flatMap((config) => {
		if (config.slots.length > 0) {
			return config.slots.map((slot) => ({
				title: _format(config.formatKey.replace('{slot}', slot), {
					default: config.formatKey
						.replace('{slot}', slot)
						.replace(/_/g, ' ')
						.replace(/\b\w/g, (l) => l.toUpperCase()),
				}),
				value: `cury_${config.key}_${slot}`,
			}));
		} else {
			return [
				{
					title: _format(config.formatKey, {
						default: config.formatKey
							.replace(/_/g, ' ')
							.replace(/\b\w/g, (l) => l.toUpperCase()),
					}),
					value: `cury_${config.key}`,
				},
			];
		}
	});

	return [selectActionPlaceholder, ...actionOptions];
};

/**
 * Generate slot options for Cury diffuser
 */
export const generateCurySlotOptions = (): SelectOptionPropsType[] => {
	const _format = get(format);

	return [
		{ title: _format('left_slot', { default: 'Left' }), value: 'left' },
		{ title: _format('right_slot', { default: 'Right' }), value: 'right' },
	];
};

/**
 * Action creation helper functions
 */
export const createSlotAction = (actionName: string, value: any, slotName: string): CuryActionData => ({
	name: actionName,
	value,
	slot: slotName,
});

export const createIntensityAction = (intensityValue: number, slotName: string): CuryActionData => ({
	name: 'cury_intensity',
	value: intensityValue,
	slot: slotName,
});

export const createAmbientAction = (
	ambientEnabled: boolean,
	ambientBrightness: number,
	ambientColor: number[],
): CuryActionData => ({
	name: 'cury_ambient',
	value: {
		enable: ambientEnabled,
		brightness: ambientBrightness,
		red: ambientColor[0],
		green: ambientColor[1],
		blue: ambientColor[2],
	},
});

/**
 * Optimized action mapping for Cury diffuser
 */
export const createCuryActionMappings = (
	intensity: number,
	slot: string,
	ambientEnabled: boolean,
	ambientBrightness: number,
	ambientColor: number[],
): Record<string, () => CuryActionData[]> => ({
	0: () => [], // Placeholder - no action
	cury_on_left: () => [createSlotAction('cury_on', true, 'left')],
	cury_on_right: () => [createSlotAction('cury_on', true, 'right')],
	cury_off_left: () => [createSlotAction('cury_on', false, 'left')],
	cury_off_right: () => [createSlotAction('cury_on', false, 'right')],
	cury_boost_left: () => [createSlotAction('cury_boost', true, 'left')],
	cury_boost_right: () => [createSlotAction('cury_boost', true, 'right')],
	cury_stop_left: () => [createSlotAction('cury_stop', false, 'left')],
	cury_stop_right: () => [createSlotAction('cury_stop', false, 'right')],
	cury_set: () => [createIntensityAction(intensity, slot)],
	cury_setconfig: () => [createAmbientAction(ambientEnabled, ambientBrightness, ambientColor)],
});

/**
 * Action restoration mapping for initialization
 */
export const createCuryActionRestorationMap = (
	actionOptions: SelectOptionPropsType[],
): Record<string, (action: any) => any> => ({
	cury_on: (action: any) => {
		const isLeft = action.slot === 'left';
		const isOn = action.value !== false;
		const actionType = isOn
			? isLeft
				? 'cury_on_left'
				: 'cury_on_right'
			: isLeft
				? 'cury_off_left'
				: 'cury_off_right';
		return {
			selectedAction: actionOptions.find((opt) => opt.value === actionType) || actionOptions[0],
			slot: action.slot || 'left',
		};
	},
	cury_stop: (action: any) => {
		const isLeft = action.slot === 'left';
		const actionType = isLeft ? 'cury_stop_left' : 'cury_stop_right';
		return {
			selectedAction: actionOptions.find((opt) => opt.value === actionType) || actionOptions[0],
			slot: action.slot || 'left',
		};
	},
	cury_boost: (action: any) => {
		const isLeft = action.slot === 'left';
		const actionType = isLeft ? 'cury_boost_left' : 'cury_boost_right';
		return {
			selectedAction: actionOptions.find((opt) => opt.value === actionType) || actionOptions[0],
			slot: action.slot || 'left',
		};
	},
	cury_intensity: (action: any) => ({
		selectedAction: actionOptions.find((opt) => opt.value === 'cury_set') || actionOptions[0],
		intensity: action.value || 50,
		slot: action.slot || 'left',
	}),
	cury_ambient: (action: any) => ({
		selectedAction: actionOptions.find((opt) => opt.value === 'cury_setconfig') || actionOptions[0],
		slot: 'left',
		ambientEnabled: action.value?.enable || false,
		ambientBrightness: action.value?.brightness || 50,
		ambientColor: [action.value?.red || 255, action.value?.green || 0, action.value?.blue || 0],
	}),
});

/**
 * Generate action preview text for Cury diffuser actions
 */
export const generateCuryActionPreview = (
	selectedAction: SelectOptionPropsType,
	slot: string,
	intensity: number,
	ambientEnabled: boolean,
	ambientBrightness: number,
): string => {
	const _format = get(format);

	if (!selectedAction?.value || selectedAction.value === 0) return '';

	// Preview mapping for different action types
	const previewMappings: Record<string, () => string> = {
		cury_on_left: () =>
			_format('scene_action_cury_on_preview', {
				default: 'Turn on {slot} slot',
				values: {
					slot: _format('left_slot', { default: 'left' }).toLowerCase(),
				},
			}),
		cury_on_right: () =>
			_format('scene_action_cury_on_preview', {
				default: 'Turn on {slot} slot',
				values: {
					slot: _format('right_slot', { default: 'right' }).toLowerCase(),
				},
			}),
		cury_off_left: () =>
			_format('scene_action_cury_off_preview', {
				default: 'Turn off {slot} slot',
				values: {
					slot: _format('left_slot', { default: 'left' }).toLowerCase(),
				},
			}),
		cury_off_right: () =>
			_format('scene_action_cury_off_preview', {
				default: 'Turn off {slot} slot',
				values: {
					slot: _format('right_slot', { default: 'right' }).toLowerCase(),
				},
			}),
		cury_boost_left: () =>
			_format('scene_action_cury_boost_preview', {
				default: 'Boost {slot} slot to maximum intensity (100%)',
				values: {
					slot: _format('left_slot', { default: 'left' }).toLowerCase(),
				},
			}),
		cury_boost_right: () =>
			_format('scene_action_cury_boost_preview', {
				default: 'Boost {slot} slot to maximum intensity (100%)',
				values: {
					slot: _format('right_slot', { default: 'right' }).toLowerCase(),
				},
			}),
		cury_stop_left: () =>
			_format('scene_action_cury_stop_boost_preview', {
				default: 'Stop boost {slot} slot',
				values: {
					slot: _format('left_slot', { default: 'left' }).toLowerCase(),
				},
			}),
		cury_stop_right: () =>
			_format('scene_action_cury_stop_boost_preview', {
				default: 'Stop boost {slot} slot',
				values: {
					slot: _format('right_slot', { default: 'right' }).toLowerCase(),
				},
			}),
		cury_set: () => {
			const slotText = _format(slot === 'left' ? 'left_slot' : 'right_slot', {
				default: slot === 'left' ? 'Left' : 'Right',
			});
			return _format('scene_action_cury_intensity_preview', {
				default: 'Set {slot} slot intensity to {intensity}%',
				values: { slot: slotText.toLowerCase(), intensity },
			});
		},
		cury_setconfig: () => {
			if (ambientEnabled) {
				return _format('scene_action_cury_ambient_enabled_preview', {
					default: 'Enable ambient light with brightness {brightness}% and custom color',
					values: { brightness: ambientBrightness || 50 },
				});
			} else {
				return _format('scene_action_cury_ambient_disabled_preview', {
					default: 'Disable ambient light',
				});
			}
		},
	};

	const previewGenerator = previewMappings[selectedAction.value as string];
	return previewGenerator ? previewGenerator() : '';
};

/**
 * Initialize Cury diffuser component from existing actions or defaults
 */
export const initializeCuryFromActions = (
	active_actions: CuryActionData[],
	actionOptions: SelectOptionPropsType[],
): CuryInitValues => {
	if (active_actions?.length > 0) {
		const existingAction = active_actions[0];

		if (import.meta.env.DEV) {
			console.log('CuryDiffuser - Restoring from existing action:', existingAction);
		}

		const actionRestorationMap = createCuryActionRestorationMap(actionOptions);
		const restorer = actionRestorationMap[existingAction.name];

		if (restorer) {
			return {
				intensity: 50,
				ambientEnabled: false,
				ambientBrightness: 50,
				ambientColor: [255, 0, 0],
				...restorer(existingAction),
			};
		}
	}

	// Default values
	return {
		selectedAction: actionOptions[0],
		intensity: 50,
		slot: 'left',
		ambientEnabled: false,
		ambientBrightness: 50,
		ambientColor: [255, 0, 0],
	};
};

/**
 * Load UI state from active_actions
 */
export const loadCuryUIState = (
	active_actions: CuryActionData[],
	actionOptions: SelectOptionPropsType[],
	slotOptions: SelectOptionPropsType[],
) => {
	if (active_actions.length === 0) {
		return {
			selectedAction: actionOptions[0],
			intensity: 50,
			slot: 'left',
			selectedSlot: slotOptions[0],
			ambientEnabled: false,
			ambientBrightness: 50,
			ambientColor: [255, 0, 0],
		};
	}

	const action = active_actions[0];
	let selectedAction = actionOptions[0];
	let intensity = 50;
	let slot = 'left';
	let ambientEnabled = false;
	let ambientBrightness = 50;
	let ambientColor = [255, 0, 0];

	if (action.name === 'cury_on') {
		const onOff = action.value === false ? 'off' : 'on';
		selectedAction =
			actionOptions.find((opt) => opt.value === `cury_${onOff}_${action.slot}`) ||
			actionOptions[0];
		slot = action.slot || 'left';
	} else if (action.name === 'cury_boost') {
		selectedAction =
			actionOptions.find((opt) => opt.value === `cury_boost_${action.slot}`) || actionOptions[0];
		slot = action.slot || 'left';
	} else if (action.name === 'cury_stop') {
		selectedAction =
			actionOptions.find((opt) => opt.value === `cury_stop_${action.slot}`) || actionOptions[0];
		slot = action.slot || 'left';
	} else if (action.name === 'cury_intensity') {
		selectedAction = actionOptions.find((opt) => opt.value === 'cury_set') || actionOptions[0];
		intensity = action.value || 50;
		slot = action.slot || 'left';
	} else if (action.name === 'cury_ambient') {
		selectedAction = actionOptions.find((opt) => opt.value === 'cury_setconfig') || actionOptions[0];
		ambientEnabled = action.value?.enable || false;
		ambientBrightness = action.value?.brightness || 50;
		ambientColor = [action.value?.red || 255, action.value?.green || 0, action.value?.blue || 0];
	}

	const selectedSlot = slotOptions.find((opt) => opt.value === slot) || slotOptions[0];

	return {
		selectedAction,
		intensity,
		slot,
		selectedSlot,
		ambientEnabled,
		ambientBrightness,
		ambientColor,
	};
};
